import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "list", "latitude", "longitude", "selectedFlag", "error"]
  static values = { apiKey: String, updateUrl: { type: Boolean, default: false } }

  connect() {
    this.debounced = null
    this._handleLocationUpdated = (event) => this.locationUpdated(event)
    document.addEventListener("location:updated", this._handleLocationUpdated)
  }

  disconnect() {
    document.removeEventListener("location:updated", this._handleLocationUpdated)
  }

  inputChanged() {
    const query = this.inputTarget.value.trim()
    if (!query) {
      this.clearSelection()
      this.dispatchAddressCleared()
      this.clearList()
      return
    }
    this.clearSelection()
    if (!this.apiKeyValue) {
      return
    }
    clearTimeout(this.debounced)
    this.debounced = setTimeout(() => this.fetchResults(query), 250)
  }

  async fetchResults(query) {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${this.apiKeyValue}&autocomplete=true&types=address,place&limit=5&language=fr`
    try {
      const response = await fetch(url)
      if (!response.ok) {
        this.clearList()
        return
      }
      const data = await response.json()
      this.renderList(data.features || [])
    } catch (_e) {
      this.clearList()
    }
  }

  renderList(features) {
    this.listTarget.innerHTML = ""
    if (features.length === 0) {
      this.listTarget.classList.add("hidden")
      return
    }
    features.forEach((feature) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
      const fullLabel = feature.place_name || this.shortLabelFor(feature)
      item.textContent = fullLabel
      item.dataset.lat = feature.center[1]
      item.dataset.lon = feature.center[0]
      item.dataset.label = fullLabel
      item.addEventListener("click", () => this.selectItem(item))
      this.listTarget.appendChild(item)
    })
    this.listTarget.classList.remove("hidden")
  }

  shortLabelFor(feature) {
    const text = feature.text
    const city = feature.context?.find((c) => c.id?.startsWith("place"))?.text
    return [text, city].filter(Boolean).join(", ")
  }

  selectItem(item) {
    this.inputTarget.value = item.dataset.label
    this.latitudeTarget.value = item.dataset.lat
    this.longitudeTarget.value = item.dataset.lon
    this.selectedFlagTarget.value = "1"
    this.clearError()
    this.clearList()
    this.dispatchAddressSelected(item.dataset.lat, item.dataset.lon)
    this.updateUrlParams()
  }

  clearSelection() {
    if (this.hasSelectedFlagTarget) {
      this.selectedFlagTarget.value = ""
    }
    if (this.hasLatitudeTarget) this.latitudeTarget.value = ""
    if (this.hasLongitudeTarget) this.longitudeTarget.value = ""
  }

  clearList() {
    this.listTarget.innerHTML = ""
    this.listTarget.classList.add("hidden")
  }

  locationUpdated(event) {
    if (!event?.detail) return
    if (this.selectedFlagTarget.value === "1") return
    if (this.inputTarget.value.trim() !== "") return
    const { lat, lon } = event.detail
    if (lat === undefined || lon === undefined) return
    this.latitudeTarget.value = String(lat)
    this.longitudeTarget.value = String(lon)
    this.updateUrlParams()
  }

  dispatchAddressSelected(lat, lon) {
    document.dispatchEvent(
      new CustomEvent("address:selected", { detail: { lat: parseFloat(lat), lon: parseFloat(lon) } })
    )
  }

  dispatchAddressCleared() {
    document.dispatchEvent(new CustomEvent("address:cleared"))
    this.updateUrlParams()
  }

  updateUrlParams() {
    if (!this.updateUrlValue) return
    const url = new URL(window.location.href)
    const address = this.inputTarget.value.trim()
    if (address) {
      url.searchParams.set("address", address)
      if (this.latitudeTarget.value && this.longitudeTarget.value) {
        url.searchParams.set("latitude", this.latitudeTarget.value)
        url.searchParams.set("longitude", this.longitudeTarget.value)
        url.searchParams.set("address_selected", this.selectedFlagTarget.value || "1")
      }
    } else if (this.latitudeTarget.value && this.longitudeTarget.value) {
      url.searchParams.set("latitude", this.latitudeTarget.value)
      url.searchParams.set("longitude", this.longitudeTarget.value)
      url.searchParams.delete("address")
      url.searchParams.delete("address_selected")
    } else {
      url.searchParams.delete("address")
      url.searchParams.delete("latitude")
      url.searchParams.delete("longitude")
      url.searchParams.delete("address_selected")
    }
    window.history.replaceState({}, "", url.toString())
  }

  validateSelection(event) {
    if (this.inputTarget.value.trim() === "") return
    if (this.selectedFlagTarget.value === "1") return
    event.preventDefault()
    this.showError("Sélectionnez une adresse dans la liste.")
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
